import { useCallback, useState } from 'react';
import { defaultLimit } from 'views/CRM/shared/utils/pagination';
import AccountService from '../services/accountService';

const formatColumnName = (name) => {
  return name.replace(/([A-Z])/g, '_$1').toLowerCase();
};

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @typedef {Object} Account
 * @property {string} id
 * @property {string} name
 * @property {string} phone
 * @property {string} createdAt
 * @property {string} createdByName
 * @property {string} lastUpdatedAt
 * @property {string} recordTypeId
 */

/**
 * Custom hook to manage the list of accounts.
 * @param {Object} params
 * @param {string} params.recordTypeId
 * @param {SortConfig} params.sortConfig
 * @returns {{
 *   accounts: Account[] | null,
 *   totalAccounts: number,
 *   isLoadingAccounts: boolean,
 *   getAccounts: (page?: number, params?: Object) => void,
 *   clearAccounts: () => void
 * }}
 */
export const useListAccounts = ({ recordTypeId, sortConfig }) => {
  const [accounts, setAccounts] = useState(null);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  /**
   * Fetch accounts from the server.
   * @param {number} [page=1]
   * @param {Object} [params={}]
   */
  const getAccounts = useCallback(
    (page = 1, params = {}) => {
      setIsLoadingAccounts(true);

      const orderType =
        sortConfig?.direction === null ||
        sortConfig?.direction === undefined ||
        // Algumas páginas estão passando `ascending` enquanto outras passam `ascendant`. Para evitar modificar muitos arquivos, aceita-se ambos os modos.
        sortConfig?.direction === 'ascending' ||
        sortConfig?.direction === 'ascendant'
          ? 'ASC'
          : 'DESC';

      const orderBy = sortConfig?.key
        ? formatColumnName(sortConfig?.key)
        : sortConfig?.key;

      AccountService.getAccounts(
        recordTypeId,
        { ...params, orderType, orderBy },
        {
          page,
          limit: defaultLimit
        }
      )
        .then((response) => {
          setAccounts(response.data.data);
          setTotalAccounts(response.data.totalRegisters);
        })
        .finally(() => setIsLoadingAccounts(false));
    },
    [recordTypeId, sortConfig]
  );

  /**
   * Clear the accounts data.
   */
  const clearAccounts = useCallback(() => {
    setAccounts(null);
    setTotalAccounts(0);
    setIsLoadingAccounts(false);
  }, []);

  return {
    accounts,
    totalAccounts,
    isLoadingAccounts,
    getAccounts,
    clearAccounts
  };
};
